// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify/Notify";
import ProductDescription from "@bg/Overrides/Components/ProductDescription";
import ProductImage from "@bg/Overrides/Components/ProductImage";
import {
    CartLineCardExpandedStyles,
    cartLineCardExpandedStyles,
} from "@bg/Overrides/Widgets/Cart/CartLineCardExpanded";
import CartLineNotes from "@bg/Overrides/Widgets/Cart/CartLineNotes";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability from "@insite/content-library/Components/ProductAvailability";
import ProductBrand from "@insite/content-library/Components/ProductBrand";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import CartLineQuantity from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { GridItemProps } from "@insite/mobius/GridItem";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    isOnePageCheckout: getCurrentPage(state).type === "OnePageCheckoutPage",
    // XNG-Changes
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

// XNG-Changes
// Add mapDispatchToProps
const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedComponentStyles extends CartLineCardExpandedStyles {
    costCodeValueText?: TypographyPresentationProps;
}

export const cartLineCardExpandedComponentStyles: CartLineCardExpandedComponentStyles = {
    ...cartLineCardExpandedStyles,
};

const CartLineCardExpanded = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    // XNG-Change
    isOnePageCheckout,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedComponentStyles, extendedStyles));

    // XNG-Changes
    const toasterContext = useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    return (
        <StyledWrapper
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.rightSection}>
                <StyledWrapper {...styles.imageWrapper}>
                    <ProductImage showQuickView={false} product={cartLine} extendedStyles={styles.productImage} />
                </StyledWrapper>
                <StyledWrapper {...styles.descAndNumbersWrapper}>
                    <StyledWrapper {...styles.productBrandAndDescriptionGridItem}>
                        <ProductDescription
                            lines={2}
                            lineHeight={22}
                            product={cartLine}
                            extendedStyles={styles.productDescription}
                        />
                    </StyledWrapper>
                    {/* {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                        <StyledWrapper {...styles.configurationGridItem}>
                            {cartLine.sectionOptions!.map(option => (
                                <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                    {`${option.sectionName}:${option.optionName}`}
                                </Typography>
                            ))}
                        </StyledWrapper>
                    )} */}
                    <StyledWrapper {...styles.productPartNumbersWrapper}>
                        <ProductPartNumbers
                            productNumber={cartLine?.properties?.sku || cartLine?.erpNumber}
                            customerProductNumber={cartLine.customerName}
                            manufacturerItem={cartLine.manufacturerItem}
                            extendedStyles={styles.productPartNumbers}
                        />
                    </StyledWrapper>
                    {cartLine.isRestricted && (
                        <Notify
                            messageType="danger"
                            body={
                                <Typography data-test-selector="cartline_restrictedMessage">
                                    {translate("Restricted product")}
                                </Typography>
                            }
                        />
                    )}
                    {!cartLine.isActive && (
                        <Notify
                            messageType="danger"
                            body={
                                <Typography data-test-selector="cartline_inActiveProductMessage">
                                    {translate("Inactive Product")}
                                </Typography>
                            }
                        />
                    )}
                    {!cartLine.quoteRequired && (
                        <ProductAvailability
                            productId={cartLine.productId!}
                            availability={cartLine.availability!}
                            unitOfMeasure={cartLine.unitOfMeasure}
                            trackInventory={cartLine.trackInventory}
                            extendedStyles={styles.productAvailability as any}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.leftSection}>
                <StyledWrapper {...styles.priceQtywrapper} className={editable ? "" : "not-editable"}>
                    {!cart.cartNotPriced && (
                        <ProductPrice
                            product={cartLine}
                            showSavings={true}
                            showSavingsAmount={showSavingsAmount}
                            showSavingsPercent={showSavingsPercent}
                            extendedStyles={styles.productPrice}
                            label={translate("Price/Unit")}
                        />
                    )}

                    <StyledWrapper {...styles.quantityGridItem} data-test-selector="orderConformation_Quantity">
                        <CartLineQuantity cart={cart} editable={editable} extendedStyles={styles.quantity} />
                    </StyledWrapper>
                    {!cartLine.quoteRequired &&
                        !cart.cartNotPriced &&
                        cartLine.pricing &&
                        (showPricing || isCheckoutReviewAndSubmitPage) && (
                            <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                                <Typography as="legend">{translate("Total")}</Typography>
                                <Typography
                                    {...styles.extendedUnitNetPriceText}
                                    data-test-selector="cartline_extendedUnitNetPrice"
                                >
                                    {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay}
                                </Typography>
                                {enableVat && (
                                    <>
                                        <Typography as="p" {...styles.vatLabelText}>
                                            {vatPriceDisplay === "DisplayWithVat" ||
                                            vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                                : translate("Ex. VAT")}
                                        </Typography>
                                        {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                            <>
                                                <Typography {...styles.subtotalWithoutVatText}>
                                                    {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                </Typography>
                                                <Typography as="p" {...styles.vatLabelText}>
                                                    {translate("Ex. VAT")}
                                                </Typography>
                                            </>
                                        )}
                                    </>
                                )}
                            </StyledWrapper>
                        )}
                </StyledWrapper>
                <StyledWrapper {...styles.listAndRemoveWrapper}>
                    <CartLineNotes
                        cart={cart}
                        editable={editable}
                        label={translate("Line Notes")}
                        extendedStyles={styles.cartLineNotes}
                        // XNG-Change
                        showAddToList={cartLine.canAddToWishlist}
                        addToListHandler={addToListClickHandler}
                    />
                </StyledWrapper>
                {/* {cart.showCostCode && cartLine.costCode && (
                    <StyledWrapper>
                        <Typography {...styles.costCodeValueText} as="p">
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}:</Typography>
                            {cartLine.costCode}
                        </Typography>
                    </StyledWrapper>
                )} */}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
